import { useMemo, useState } from 'react';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { declarerActif, getImpact } from '@/shared/api/bridge';
import { declarables, libelleVerrou, ranger } from '@/features/bridge/bridge-etat';
import ConfirmationImpact from '@/features/bridge/components/ConfirmationImpact';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

function Puce({ actif, children, surClic }) {
  return (
    <button type="button" aria-pressed={actif} onClick={surClic}
            className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${actif
              ? 'border-neutral-800 bg-neutral-800 text-white'
              : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'}`}>
      {children}
    </button>
  );
}

/**
 * Les actifs qu'on peut encore déclarer à cet agrégateur, et le geste qui les déclare.
 *
 * DEUX FILTRES RAPIDES, PARCE QUE CE SONT LES DEUX QUESTIONS QU'ON SE POSE
 *
 * Un actif sans mandat sort du périmètre au premier recalcul ; un actif sans
 * intégration répond FAILED à un ordre. Déclarer quand même reste permis (le matériel
 * arrive parfois après), mais on veut pouvoir ne regarder que ce qui servira.
 *
 * UN ACTIF À LA FOIS, DEPUIS LE 08/10/2026
 *
 * Une déclaration est définitive (07/10). La sélection multiple et sa case « tout
 * cocher » permettaient de verrouiller tout le parc, actifs sans mandat compris, sur une
 * seule confirmation : elles sont supprimées (relecture de la PR #235). Chaque ligne a
 * son bouton, son impact sur le revenu et son avertissement « définitif ». Un actif déjà
 * déclaré à un AUTRE agrégateur reste visible, grisé, sans bouton.
 */
export default function ListeCandidats({ agregateur, label, candidats, erreur, surDeclaration }) {
  const [filtre, setFiltre] = useState('');
  const [avecMandat, setAvecMandat] = useState(false);
  const [avecIntegration, setAvecIntegration] = useState(false);
  const [ref, setRef] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [erreurGeste, setErreurGeste] = useState(null);

  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    return ranger(candidats)
      .filter((c) => !avecMandat || c.sous_mandat)
      .filter((c) => !avecIntegration || c.integration)
      .filter((c) => !q || [c.type, c.marque, c.modele, c.client_nom, c.client_ville]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [candidats, filtre, avecMandat, avecIntegration]);

  const demander = async (candidat) => {
    setErreurGeste(null);
    setEnCours(true);
    try {
      const impact = await getImpact(candidat.asset_id, agregateur, false);
      setRef('');
      setConfirmation({ titre: `Déclarer ${candidat.type || 'cet actif'} à ${label}`,
                        impact, candidat });
    } catch (exc) {
      setErreurGeste(exc);
    } finally {
      setEnCours(false);
    }
  };

  const declarer = async () => {
    setEnCours(true);
    try {
      await declarerActif(agregateur, { asset_id: confirmation.candidat.asset_id, ref: ref.trim() });
      setConfirmation(null);
      await surDeclaration?.();
    } catch (exc) {
      setErreurGeste(exc);
      setConfirmation(null);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <section aria-labelledby="candidats-titre" className="space-y-2.5">
      <h3 id="candidats-titre" className="text-[14px] font-semibold text-neutral-900">
        À déclarer
        {candidats && (
          <span className="ml-1.5 font-normal tabular-nums text-neutral-500"
                data-testid="bridge-compte-candidats">{declarables(visibles).length}</span>
        )}
      </h3>

      <div className="flex flex-wrap items-center gap-2">
        <Input value={filtre} onChange={(e) => setFiltre(e.target.value)}
               placeholder="client, marque ou type" className="h-8 max-w-[220px]"
               data-testid="bridge-filtre-candidats" />
        <Puce actif={avecMandat} surClic={() => setAvecMandat((v) => !v)}>sous mandat</Puce>
        <Puce actif={avecIntegration} surClic={() => setAvecIntegration((v) => !v)}>intégration branchée</Puce>
      </div>

      <ErreurBloc erreur={erreur} testid="bridge-candidats-erreur" />
      <ErreurBloc erreur={erreurGeste} testid="bridge-declaration-erreur" />
      {candidats === null && !erreur && <Skeleton className="h-40 w-full" />}

      {candidats && visibles.length === 0 && (
        <p className="rounded-md border border-dashed border-neutral-300 px-4 py-8 text-center
                      text-[13px] text-neutral-500" data-testid="bridge-aucun-candidat">
          {filtre || avecMandat || avecIntegration
            ? 'Aucun actif ne correspond à ces filtres.'
            : "Aucun actif à déclarer : tout ce qui est éligible l'est déjà."}
        </p>
      )}

      {visibles.length > 0 && (
        <div className="max-h-[420px] overflow-auto rounded-md border border-neutral-200">
          <table className="w-full min-w-[480px] text-[13px]">
            <thead className="sticky top-0 bg-neutral-50 text-left text-[12px] text-neutral-500">
              <tr>
                <th className="py-2 pl-3 font-normal">Actif</th>
                <th className="font-normal">Client</th>
                <th className="font-normal">Mandat</th>
                <th className="font-normal">Intégration</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {visibles.map((c) => (
                <tr key={c.asset_id}
                    className={`border-t border-neutral-100 ${c.verrouille_chez ? 'bg-neutral-50 text-neutral-400' : ''}`}
                    data-testid={`bridge-candidat-${c.asset_id}`}>
                  <td className="py-1.5 pl-3">
                    <span className={c.verrouille_chez ? 'font-medium' : 'font-medium text-neutral-900'}>{c.type}</span>
                    {c.marque ? ` · ${c.marque}` : ''}
                    {c.puissance_kw ? <span className="text-neutral-500"> · {c.puissance_kw} kW</span> : null}
                    {c.verrouille_chez && (
                      <span className="block text-[11.5px] text-neutral-500"
                            data-testid={`bridge-verrou-${c.asset_id}`}>
                        {libelleVerrou(c.verrouille_chez)}
                      </span>
                    )}
                  </td>
                  <td className="text-neutral-600">
                    {c.client_nom || '—'}
                    {c.client_ville && <span className="text-neutral-400"> · {c.client_ville}</span>}
                  </td>
                  <td className={c.sous_mandat ? 'text-emerald-700' : 'text-amber-700'}>
                    {c.sous_mandat ? 'oui' : 'non'}
                  </td>
                  <td className={c.integration ? 'text-emerald-700' : 'text-amber-700'}>
                    {c.integration ? 'branchée' : 'absente'}
                  </td>
                  <td className="pr-3 text-right">
                    {!c.verrouille_chez && (
                      <button type="button" disabled={enCours} onClick={() => demander(c)}
                              className="text-[12.5px] underline underline-offset-2 disabled:opacity-50"
                              data-testid={`bridge-declarer-${c.asset_id}`}>
                        Déclarer…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmationImpact prefixe="bridge-declaration" confirmation={confirmation} enCours={enCours}
                          onAnnuler={() => setConfirmation(null)} onConfirmer={declarer}>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-600">
          Référence chez {label} (facultatif)
          <Input value={ref} onChange={(e) => setRef(e.target.value)}
                 data-testid="bridge-declaration-ref" />
        </label>
      </ConfirmationImpact>
    </section>
  );
}
